import _ from 'lodash'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import { getEnrollmentData, initializePayment, quotePayment, requestEnrollment, getRegistration } from '../actions/programRegistrationActions'
import { createUser, inviteFamilyMember } from '../actions/authActions'
import { REGISTRATION_STAGES, REGISTRATION_STAGE_BY_STEP } from '../misc/Constants'
import { isFeatureEnabled } from '../utility/featureEnablement'

const initialState = {
  status: 'inactive',
  errors: null,
  newUserErrors: {},
  enrollmentData: null,
  invitationToken: null,
  registrationStatus: null,
  selectedFamilyMember: null,
  selectedPackage: {},
  selectedPaymentMethod: null,
  selectedMembershipPlanId: null,
  paymentError: null,
  paymentSession: null,
  isProcessingPayment: false,
  acceptedMaterials: {},
  refusedMaterials: {},
  completedSteps: [],
  currentStep: null,
  isEmbedded: false,
  courseId: null,
}

const programRegistrationSlice = createSlice({
  name: 'programRegistration',
  initialState,
  reducers: {
    resetRegistration: () => initialState,
    decrementCurrentStep: (state) => {
      state.currentStep--
    },
    incrementCurrentStep: (state) => {
      state.currentStep++
    },
    incrementCurrentStepBy: (state, action) => {
      state.currentStep += action.payload
    },
    completeStep: (state, action) => {
      state.completedSteps.push(action.payload)
    },
    setSelectedFamilyMember: (state, action) => {
      state.selectedFamilyMember = action.payload
    },
    setSelectedPackage: (state, action) => {
      state.selectedPackage = action.payload
    },
    setSelectedPaymentMethod: (state, action) => {
      state.selectedPaymentMethod = action.payload
      state.paymentError = null
    },
    setSelectedMembershipPlanId: (state, action) => {
      state.selectedMembershipPlanId = action.payload
    },
    setPaymentError: (state, action) => {
      state.paymentError = action.payload
    },
    resetPaymentSession: (state) => {
      state.paymentSession = null
    },
    markSetupConfirmed: (state) => {
      if (state.paymentSession) {
        state.paymentSession.setupConfirmed = true
      }
    },
    setIsProcessingPayment: (state, action) => {
      state.isProcessingPayment = action.payload
    },
    addAcceptedMaterial: (state, action) => {
      const material = action.payload
      state.acceptedMaterials[material.id] = material
      delete state.refusedMaterials[material.id]
    },
    addRefusedMaterial: (state, action) => {
      const material = action.payload
      state.refusedMaterials[material.id] = material
      delete state.acceptedMaterials[material.id]
    },
  },
  extraReducers: {
    // Get enrollment data
    [getEnrollmentData.pending]: (state, action) => {
      state.status = 'loading enrollment data'
      state.invitationToken = action.meta.arg.invitationToken
    },
    [getEnrollmentData.fulfilled]: (state, action) => {
      state.status = 'active'
      const enrollmentData = action.payload.enrollmentData
      state.enrollmentData = enrollmentData
      state.currentStep = 0
    },
    [getEnrollmentData.rejected]: (state, action) => {
      state.status = 'error'
      state.errors = action.payload
    },

    // Request enrollment
    [requestEnrollment.pending]: state => {
      state.status = 'processing registration'
    },
    [requestEnrollment.rejected]: (state, action) => {
      state.status = 'error'
      state.errors = action.payload
    },

    // Initialize payment (pending registration + platform SetupIntent)
    [initializePayment.pending]: (state, action) => {
      state.selectedMembershipPlanId = null
      state.paymentSession = {
        requestId: action.meta.requestId,
        order: action.meta.arg,
        status: 'loading',
        registrationId: null,
        setupIntentClientSecret: null,
        setupConfirmed: false,
        quote: null,
      }
    },
    [initializePayment.fulfilled]: (state, action) => {
      if (state.paymentSession?.requestId === action.meta.requestId) {
        state.paymentSession.status = 'ready'
        state.paymentSession.registrationId = action.payload.registrationId
        state.paymentSession.setupIntentClientSecret = action.payload.setupIntentClientSecret
      }
    },
    [initializePayment.rejected]: (state, action) => {
      state.status = 'error'
      state.errors = action.payload
      state.paymentSession = null
    },

    // Quote payment (pricing lines + membership decision)
    [quotePayment.pending]: (state, action) => {
      if (state.paymentSession) {
        // a re-quote keeps the previous answer so the mounted Stripe card form survives it
        const previous = state.paymentSession.quote

        state.paymentSession.quote = {
          requestId: action.meta.requestId,
          params: {
            paymentMethod: action.meta.arg.paymentMethod,
            membershipPlanId: action.meta.arg.membershipPlanId ?? null,
          },
          status: 'loading',
          pricing: previous?.pricing ?? null,
          cardRequired: previous?.cardRequired ?? false,
          membership: previous?.membership ?? null,
        }
      }
    },
    [quotePayment.fulfilled]: (state, action) => {
      const quote = state.paymentSession?.quote

      if (quote?.requestId === action.meta.requestId) {
        quote.status = 'ready'
        quote.pricing = action.payload.pricing
        quote.cardRequired = action.payload.cardRequired
        quote.membership = action.payload.membership
      }
    },
    [quotePayment.rejected]: (state, action) => {
      if (action.payload?.notPayable) {
        state.paymentSession = null
      } else {
        state.status = 'error'
        state.errors = action.payload
      }
    },

    // Get registration (success screen)
    [getRegistration.pending]: state => {
      state.status = 'processing registration'
    },
    [getRegistration.fulfilled]: (state, action) => {
      if (action.payload.registration.status === 'pending') {
        state.status = 'processing registration'
      } else {
        state.status = 'finished'
        state.registrationStatus = action.payload.registration.status
        state.acceptedMaterials = action.payload.registration.materials
        state.selectedPackage = action.payload.registration.package
        state.selectedFamilyMember = action.payload.registration.user
        state.selectedPaymentMethod = action.payload.registration.paymentMethod
        state.courseId = action.payload.registration.courseId
        state.isEmbedded = action.payload.registration.createdBy === 'mobile_registration'
      }
    },
    [getRegistration.rejected]: (state, action) => {
      state.status = 'error'
      state.errors = action.payload
    },

    // Create new user
    [createUser.pending]: state => {
      state.status = 'creating user'
    },
    [createUser.fulfilled]: (state, action) => {
      const user = action.payload.user

      state.enrollmentData.familyMembers.push(user)
      state.selectedFamilyMember = user
      state.status = 'active'
    },
    [createUser.rejected]: state => {
      state.status = 'active'
    },

    // Invite family member
    [inviteFamilyMember.pending]: state => {
      state.status = 'creating user'
    },
    [inviteFamilyMember.fulfilled]: (state, action) => {
      const user = action.payload.user

      state.enrollmentData.familyMembers.push(user)
      state.status = 'active'
    },
    [inviteFamilyMember.rejected]: state => {
      state.status = 'active'
    },
  },
})

export const {
  resetRegistration,
  decrementCurrentStep,
  incrementCurrentStep,
  incrementCurrentStepBy,
  completeStep,
  setSelectedFamilyMember,
  setSelectedPackage,
  setSelectedPaymentMethod,
  setSelectedMembershipPlanId,
  setPaymentError,
  resetPaymentSession,
  markSetupConfirmed,
  setIsProcessingPayment,
  addAcceptedMaterial,
  addRefusedMaterial,
} = programRegistrationSlice.actions

export const selectAcceptedMaterialsIds = createSelector(
  state => state.programRegistration.acceptedMaterials,
  acceptedMaterials => _.map(Object.keys(acceptedMaterials), Number)
)

export const selectRefusedMaterialsIds = createSelector(
  state => state.programRegistration.refusedMaterials,
  refusedMaterials => _.map(Object.keys(refusedMaterials), Number)
)

export const selectRegistrationOrder = createSelector(
  state => state.programPresentation.course?.id,
  state => state.programRegistration.selectedFamilyMember?.id,
  state => state.programRegistration.selectedPackage,
  selectAcceptedMaterialsIds,
  (courseId, userId, selectedPackage, materialsIds) => ({
    courseId,
    userId,
    packageId: selectedPackage.id,
    packagePrice: selectedPackage.price,
    materialsIds,
  })
)

export const selectConsumerPlan = state => isFeatureEnabled(state.programPresentation.course?.community || {}, 'consumer_plan')

export const selectQuote = state => state.programRegistration.paymentSession?.quote

const COVERING_MEMBERSHIP_STATUSES = ['active', 'past_due']

export const selectMembershipCovered = state =>
  COVERING_MEMBERSHIP_STATUSES.includes(state.programPresentation.course?.membership?.family?.status)

export const selectMembershipUnavailableOnCourse = state => {
  const membership = state.programPresentation.course?.membership

  return !!selectConsumerPlan(state) && !!membership && !selectMembershipCovered(state) && _.isEmpty(membership.plans)
}

export const selectQuoteParams = createSelector(
  state => state.programRegistration.selectedPaymentMethod,
  state => state.programRegistration.selectedMembershipPlanId,
  (paymentMethod, membershipPlanId) => ({ paymentMethod, membershipPlanId })
)

export const selectRegistrationSteps = createSelector(
  state => state.programPresentation.course?.materials,
  selectConsumerPlan,
  state => selectQuote(state)?.membership?.status,
  (materials, consumerPlan, membershipStatus) => [
    'programDetails',
    ...(_.isEmpty(materials) ? [] : ['programMaterials']),
    ...(consumerPlan && (!membershipStatus || membershipStatus === 'purchase') ? ['membership'] : []),
    'checkout',
  ]
)

export const selectRegistrationStage = createSelector(
  state => state.programRegistration.status,
  selectRegistrationSteps,
  state => state.programRegistration.currentStep,
  (status, steps, currentStep) => {
    // the completed page is not one of the screens, so it reports the last stage directly
    if (status === 'finished') {
      return REGISTRATION_STAGES.length - 1
    }

    const stage = REGISTRATION_STAGE_BY_STEP[steps[currentStep]]

    return stage ? REGISTRATION_STAGES.indexOf(stage) : 0
  }
)

// How far the payer has got, which never goes back when they do: the stepper's ticks read this, not the position.
export const selectCompletedRegistrationStages = createSelector(
  state => state.programRegistration.status,
  selectRegistrationSteps,
  state => state.programRegistration.completedSteps,
  (status, steps, completedSteps) => {
    if (status === 'finished') {
      return REGISTRATION_STAGES.length
    }

    const firstUnfinished = REGISTRATION_STAGES.findIndex(stage => steps.some(
      (step, index) => REGISTRATION_STAGE_BY_STEP[step] === stage && !completedSteps.includes(index)
    ))

    return firstUnfinished === -1 ? REGISTRATION_STAGES.length : firstUnfinished
  }
)

export const withMaterials = createSelector(
  state => state.programRegistration.acceptedMaterials,
  acceptedMaterials => !_.isEmpty(acceptedMaterials)
)

export const validProgramDetailsStep = createSelector(
  state => ({
    selectedFamilyMember: state.programRegistration.selectedFamilyMember,
    selectedPackage: state.programRegistration.selectedPackage,
    coursePackages: state.programRegistration.enrollmentData?.coursePackages,
  }),
  programDetailsData => programDetailsData.selectedFamilyMember
    && (!_.isEmpty(programDetailsData.selectedPackage) || programDetailsData.coursePackages?.length === 0)
)

export const validProgramMaterialsStep = createSelector(
  selectAcceptedMaterialsIds,
  selectRefusedMaterialsIds,
  state => state.programRegistration.enrollmentData?.courseMaterials,
  (acceptedMaterialsIds, refusedMaterialsIds, courseMaterials) =>
    acceptedMaterialsIds?.length + refusedMaterialsIds?.length === courseMaterials?.length
)

export default programRegistrationSlice.reducer
